import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgUsageLogRow, OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmtDateTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/**
 * Usage History — org tier, read-only. Cross-school mobile API usage, one
 * row per school + endpoint (same "Usage by Endpoint" breakdown the
 * school-tier UsageHistoryPage's Overview/Endpoints tabs show), matching
 * the Departments/Finance Report pattern. Raw per-request logs and the
 * per-user drill-down stay school-tier only — enter a school to see those
 * (OrgLayout's own "enter a school" precedent), since a cross-school raw
 * log fetch has no natural bound the way an endpoint-count table does.
 */
export function OrgUsageHistoryPage() {
  const [schoolId, setSchoolId] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-usage-history'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const params = { schoolId: schoolId || undefined, dateFrom, dateTo };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-usage-logs', params],
    queryFn: () => OrgReportsApi.usageLogs(params),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportUsageLogs(format, params);
    } finally {
      setBusy(null);
    }
  };

  const totalHits = rows.reduce((sum, r) => sum + r.hits, 0);
  const distinctSchools = new Set(rows.map((r) => r.schoolId)).size;

  return (
    <>
      <PageHeader
        title="Usage History"
        description="Mobile app API usage — hits per endpoint, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-slate-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatChip label="Total Hits" value={totalHits} />
        <StatChip label="Schools with Activity" value={distinctSchools} />
        <StatChip label="Endpoint Rows" value={rows.length} />
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Endpoint</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Hits</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Last Hit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                    No mobile API hits for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: OrgUsageLogRow, i) => (
                  <tr key={`${r.schoolId}-${r.method}-${r.routePattern}-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.schoolName}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-900">
                      <span className="text-brand-600">{r.method}</span> {r.routePattern}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{r.hits}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.avgDurationMs}ms</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDateTime(r.lastHitAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function StatChip({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
